import { useMemo, useState } from 'react';
import {
  useAdministrativeTypesQuery,
  useCertificationsQuery,
  useLanguagesQuery,
  useSocialWorkerTypesQuery,
  useSpecialitiesQuery,
  useSpecialistTypesQuery,
  useTaxStatusesQuery,
} from '../api/queries/referenceData';
import { PRACTITIONER_ACTIVITIES } from '../../features/practitioner/lib/activities';

export type ReferenceDataKey =
  | 'administrativeTypes'
  | 'languages'
  | 'specialities'
  | 'certifications'
  | 'specialistTypes'
  | 'taxStatuses'
  | 'socialWorkerTypes'
  | 'activities';

export const ALL_REFERENCE_DATA_KEYS: ReferenceDataKey[] = [
  'administrativeTypes',
  'languages',
  'specialities',
  'certifications',
  'specialistTypes',
  'taxStatuses',
  'socialWorkerTypes',
  'activities',
];

const noopReferenceSetter = () => undefined;

export interface UseReferenceDataOptions {
  keys?: ReferenceDataKey[];
}

/** Données référentielles partagées (TanStack Query, sans provider React). */
export function useReferenceData(options?: UseReferenceDataOptions) {
  const keys = options?.keys ?? ALL_REFERENCE_DATA_KEYS;
  const include = (key: ReferenceDataKey) => keys.includes(key);

  const { data: administrativeTypes = [], isLoading: administrativeTypesLoading } =
    useAdministrativeTypesQuery({ enabled: include('administrativeTypes') });
  const { data: languages = [], isLoading: languagesLoading } = useLanguagesQuery({
    enabled: include('languages'),
  });
  const { data: specialities = [], isLoading: specialitiesLoading } = useSpecialitiesQuery({
    enabled: include('specialities'),
  });
  const { data: certifications = [], isLoading: certificationsLoading } =
    useCertificationsQuery({ enabled: include('certifications') });
  const { data: specialistTypes = [], isLoading: specialistTypesLoading } =
    useSpecialistTypesQuery({ enabled: include('specialistTypes') });
  const { data: taxStatuses = [], isLoading: taxStatusesLoading } = useTaxStatusesQuery({
    enabled: include('taxStatuses'),
  });
  const { data: socialWorkerTypes = [], isLoading: socialWorkerTypesLoading } =
    useSocialWorkerTypesQuery({ enabled: include('socialWorkerTypes') });
  const [activities, setActivities] = useState(
    include('activities') ? [...PRACTITIONER_ACTIVITIES] : [],
  );

  const isLoading =
    (include('administrativeTypes') && administrativeTypesLoading) ||
    (include('languages') && languagesLoading) ||
    (include('specialities') && specialitiesLoading) ||
    (include('certifications') && certificationsLoading) ||
    (include('specialistTypes') && specialistTypesLoading) ||
    (include('taxStatuses') && taxStatusesLoading) ||
    (include('socialWorkerTypes') && socialWorkerTypesLoading);

  return useMemo(
    () => ({
      languages,
      setLanguages: noopReferenceSetter,
      specialities,
      setSpecialities: noopReferenceSetter,
      administrativeTypes,
      setAdministrativeTypes: noopReferenceSetter,
      activities,
      setActivities,
      certifications,
      setCertifications: noopReferenceSetter,
      specialistTypes,
      setSpecialistTypes: noopReferenceSetter,
      taxStatuses,
      socialWorkerTypes,
      isLoading,
    }),
    [
      languages,
      specialities,
      administrativeTypes,
      activities,
      certifications,
      specialistTypes,
      taxStatuses,
      socialWorkerTypes,
      isLoading,
    ],
  );
}
